/**
 * LicensorConsoleOverview — OverviewTab for LicensorConsole.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { SectionHeader, Skeleton, ErrorBanner } from './LicensorConsolePrimitives';
import { API, apiFetch } from '../lib/api';

export function OverviewTab() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiFetch(`${API}/licensor/overview`)
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (d.error) setError(d.error); else setData(d);
      })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => load(), [load]);

  if (loading) return <Skeleton label="Loading overview..." />;
  if (error) return <ErrorBanner error={error} onRetry={load} />;
  const c = data?.counts || {};
  const fmt    = (n) => (n ?? 0).toLocaleString();
  const fmtINR = (n) => '₹ ' + (Number(n) || 0).toLocaleString();

  const stats = [
    { label: 'Total Clients',           value: fmt(c.TotalClients),          sub: 'WebApp.ClientMaster',                           color: '#3b82f6' },
    { label: 'Active Clients',          value: fmt(c.ActiveClients),         sub: 'Status = Active',                               color: '#10b981' },
    { label: 'Clients With license_id', value: fmt(c.ClientsWithLicenseId),  sub: 'SaaS auto-issued',                              color: '#8b5cf6' },
    { label: 'Total Licenses',          value: fmt(c.TotalLicenses),         sub: 'ClientMaster_License',                          color: '#3b82f6' },
    { label: 'Active Licenses',         value: fmt(c.ActiveLicenses),        sub: 'Status = Active',                               color: '#10b981' },
    { label: 'Expiring in 30d',         value: fmt(c.LicensesExpiring30d),   sub: 'Renewal alerts',                                color: '#f59e0b' },
    { label: 'Expired Licenses',        value: fmt(c.LicensesExpired),       sub: 'ExpiryDate < today',                            color: '#ef4444' },
    { label: 'Registry Entries',        value: fmt(c.RegistryEntries),       sub: 'LicenseRegistry',                               color: '#06b6d4' },
    { label: 'Signup Telemetry',        value: fmt(c.SignupTelemetryRows),    sub: 'ClientSignup_Metadata',                         color: '#a855f7' },
    { label: 'Active Tiers',            value: fmt(c.ActivePricingTiers),    sub: 'LicensePricing IsActive=1',                     color: '#3b82f6' },
    { label: 'System Users',            value: fmt(c.SystemUsersCount),      sub: `SuperAdmin: ${c.SuperAdminCount ?? 0}`,          color: '#0f172a' },
    { label: 'Total Revenue',           value: fmtINR(c.TotalLicenseAmount), sub: 'Sum(Amount) all licenses',                      color: '#10b981' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionHeader title="Licensor Operations Overview" badge="LIVE / READ-ONLY" onRefresh={load} />

      <div className="lc-stat-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        {stats.map((s) => (
          <div key={s.label} className="card" style={{ padding: 8 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color, fontFamily: 'monospace', marginBottom: 2 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="lc-split-main" style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 8 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Recent License Issuances</span><span className="card-badge">TOP 10</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table data-table-compact">
              <thead><tr><th>Client</th><th>Tier</th><th>Amount</th><th>Issued</th><th>Expiry</th><th>Status</th></tr></thead>
              <tbody>
                {(data?.recentLicenses || []).map((l) => (
                  <tr key={l.LicenseId || l.ClientId || l.clientId}>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: 12 }}>{l.ClientName || l.CompName || '—'}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{l.ClientId}</div>
                    </td>
                    <td><span className="badge badge-blue">{l.LicenseTier}</span></td>
                    <td style={{ fontWeight: 700 }}>{fmtINR(l.Amount)}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{l.IssuedDate ? new Date(l.IssuedDate).toLocaleDateString() : '—'}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{l.ExpiryDate ? new Date(l.ExpiryDate).toLocaleDateString() : '—'}</td>
                    <td><span className={`badge ${l.Status === 'Active' ? 'badge-green' : 'badge-amber'}`}>{l.Status}</span></td>
                  </tr>
                ))}
                {(!data?.recentLicenses || data.recentLicenses.length === 0) && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No license records yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Tier Breakdown</span><span className="card-badge">GROUPED</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table data-table-compact">
              <thead><tr><th>Tier</th><th>Total</th><th>Active</th><th>Revenue</th></tr></thead>
              <tbody>
                {(data?.tierBreakdown || []).map((t) => (
                  <tr key={t.LicenseTier}>
                    <td><span className="badge badge-blue">{t.LicenseTier}</span></td>
                    <td style={{ fontWeight: 700 }}>{t.Total}</td>
                    <td style={{ color: '#10b981', fontWeight: 700 }}>{t.Active}</td>
                    <td style={{ fontWeight: 700 }}>{fmtINR(t.Revenue)}</td>
                  </tr>
                ))}
                {(!data?.tierBreakdown || data.tierBreakdown.length === 0) && (
                  <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No tier data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
